import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type StatTileProps = {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  /** Makes the whole tile a link to the figures behind it. */
  to?: string;
  /** "negative" for a loss or something late; say so in words too (the label or hint). */
  tone?: "default" | "negative";
  /** Anything between the value and the hint, such as a proportion bar. */
  children?: ReactNode;
  className?: string;
};

/** One headline figure: a label, the value, and a line that explains it. */
export function StatTile({
  label,
  value,
  hint,
  icon: Icon,
  to,
  tone = "default",
  children,
  className,
}: StatTileProps) {
  const body = (
    <>
      <span className="flex items-center justify-between gap-2 text-[0.8125rem] font-medium text-muted-foreground">
        {label}
        {Icon && <Icon className="h-4 w-4 shrink-0 text-brand-600/80" aria-hidden />}
      </span>
      <span
        className={cn(
          "break-words font-display text-[1.625rem] font-semibold leading-[1.15] tracking-tight",
          tone === "negative" ? "text-destructive" : "text-foreground",
        )}
      >
        {value}
      </span>
      {children}
      {hint && <span className="text-xs leading-relaxed text-muted-foreground">{hint}</span>}
    </>
  );
  const frame = cn(
    "flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-4 shadow-sm md:p-5",
    className,
  );
  if (!to) return <div className={frame}>{body}</div>;
  return (
    <Link
      to={to}
      className={cn(
        frame,
        "transition-[border-color,box-shadow] duration-150 hover:border-primary/35 hover:shadow-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )}
    >
      {body}
    </Link>
  );
}
